<script setup>
import { computed, ref, watch } from 'vue'
import ChannelIcon from './ChannelIcon.vue'

const props = defineProps({
  // Image URL (Chatwoot returns one in raw_payload['contact']['thumbnail']
  // for LINE / Telegram / WhatsApp etc.). Optional — falls back to initials.
  avatarUrl:   { type: String, default: null },
  name:        { type: String, default: '' },
  // Optional small channel-type badge in the bottom-right corner.
  channelType: { type: String, default: null },
  size:        { type: Number, default: 32 },
  // Tone for the initials fallback: 'customer' (accent) | 'supplier' (warn)
  // | 'neutral' (muted).
  tone:        { type: String, default: 'customer' }
})

// Track <img> load failures so we can fall back to initials gracefully when
// Chatwoot's thumbnail URL 401s or expires.
const errored = ref(false)
watch(() => props.avatarUrl, () => { errored.value = false })

const initials = computed(() =>
  (props.name || '?')
    .split(' ')
    .map(p => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase()
)

const toneStyles = computed(() => {
  switch (props.tone) {
    case 'supplier':
      return { background: 'color-mix(in oklab, var(--warn) 18%, transparent)', color: 'var(--warn)' }
    case 'neutral':
      return { background: 'var(--surfaceAlt)', color: 'var(--inkSoft)' }
    default:
      return { background: 'var(--accentSoft)', color: 'var(--accent)' }
  }
})

const showImage = computed(() => props.avatarUrl && !errored.value)
const badgeSize = computed(() => Math.round(props.size * 0.55))
</script>

<template>
  <span
    class="relative inline-flex items-center justify-center rounded-full overflow-visible shrink-0"
    :style="{ width: size + 'px', height: size + 'px' }"
  >
    <img
      v-if="showImage"
      :src="avatarUrl"
      :alt="name"
      class="rounded-full object-cover w-full h-full"
      referrerpolicy="no-referrer"
      loading="lazy"
      @error="errored = true"
    />
    <span
      v-else
      class="w-full h-full rounded-full flex items-center justify-center fw-value"
      :style="{ ...toneStyles, fontSize: Math.round(size * 0.36) + 'px' }"
    >
      {{ initials }}
    </span>
    <span
      v-if="channelType"
      class="absolute -bottom-0.5 -right-0.5 rounded-full ring-2 ring-[color:var(--surface)] flex items-center justify-center bg-[color:var(--surface)]"
      :style="{ width: badgeSize + 'px', height: badgeSize + 'px' }"
    >
      <ChannelIcon :channel-type="channelType" :size="Math.round(badgeSize * 0.7)" />
    </span>
  </span>
</template>
